/**
 * GameLoader — the branded loading screen shown while the game (three.js chunk
 * + GLB models) loads, then fades out to reveal the game. Uses the site's
 * signature VortexBackground (canvas-2D, lightweight — no three.js) so the load
 * screen feels part of the site.
 *
 * `ready` flips true once the game has actually finished loading its assets;
 * the bar then races to 100 %, holds a beat, and fades (calls `onDone`). A
 * minimum on-screen time keeps it from flashing when assets are cached.
 */
import React, { useEffect, useRef, useState } from 'react';
import VortexBackground from '../../components/VortexBackground';
import FireParticles from '../../components/FireParticles';

const MIN_MS = 1200; // don't let the loader flash by faster than this

export default function GameLoader({ ready, onDone }) {
  const [progress, setProgress] = useState(6);
  const [fading, setFading] = useState(false);
  const mountedAt = useRef(Date.now());
  const doneRef = useRef(false);

  // Animate the bar: creep toward 90 % while loading, race to 100 % once ready.
  useEffect(() => {
    let raf;
    const tick = () => {
      setProgress((p) => {
        const cap = ready ? 100 : 90;
        const step = ready ? Math.max(2, (100 - p) * 0.14) : Math.max(0.18, (cap - p) * 0.018);
        return Math.min(cap, p + step);
      });
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [ready]);

  // Once ready (+ minimum display time), fade out and finish.
  useEffect(() => {
    if (!ready || doneRef.current) return undefined;
    const wait = Math.max(300, MIN_MS - (Date.now() - mountedAt.current));
    const t = setTimeout(() => {
      if (doneRef.current) return;
      doneRef.current = true;
      setFading(true);
      setTimeout(() => onDone && onDone(), 650); // match the CSS fade duration
    }, wait);
    return () => clearTimeout(t);
  }, [ready, onDone]);

  return (
    <div
      className="fixed inset-0 z-[300] flex flex-col items-center justify-center"
      style={{ background: '#030303', opacity: fading ? 0 : 1, transition: 'opacity 650ms ease', pointerEvents: fading ? 'none' : 'auto' }}
    >
      <VortexBackground />
      <FireParticles />
      <div className="relative flex flex-col items-center px-6" style={{ zIndex: 2 }}>
        <h1 className="font-syne font-semibold text-2xl sm:text-3xl tracking-tight text-white/90 mb-8 text-center">
          Laden
        </h1>
        <div className="w-[min(80vw,440px)]">
          <div className="h-[3px] w-full rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.08)' }}>
            <div
              className="h-full rounded-full"
              style={{
                width: `${progress}%`,
                background: 'linear-gradient(90deg,#F97316,#fbbf24)',
                boxShadow: '0 0 14px rgba(249,115,22,0.65)',
                transition: 'width 160ms ease-out',
              }}
            />
          </div>
          <div className="flex justify-end mt-3 font-mono text-[9px] uppercase tracking-widest text-white/30">
            <span>{Math.round(progress)}%</span>
          </div>
        </div>
      </div>
    </div>
  );
}
